import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Workspace } from '@/services/workspace';

interface WorkspaceState {
  workspaces: Workspace[];
  selectedWorkspace: Workspace | null;
  /**
   * Persisted user preference. We persist the workspace id instead of the whole
   * workspace object to avoid stale data across sessions/users.
   */
  selectedWorkspaceId: string | null;
  workspacesLoaded: boolean; // Track if workspaces have been fetched from API
  setWorkspaces: (workspaces: Workspace[]) => void;
  setSelectedWorkspace: (workspace: Workspace | null) => void;
  clearWorkspace: (options?: { preserveSelection?: boolean }) => void;
}

// Custom storage that uses sessionStorage for active workspace + localStorage for default
const hybridStorage = {
  getItem: (name: string): string | null => {
    // Try sessionStorage first (current tab's workspace)
    const sessionValue = sessionStorage.getItem(name);
    if (sessionValue) {
      return sessionValue;
    }

    // Fallback to localStorage (default workspace preference)
    const localValue = localStorage.getItem(name);
    if (localValue) {
      // Copy to sessionStorage for this tab
      sessionStorage.setItem(name, localValue);
      return localValue;
    }

    return null;
  },
  setItem: (name: string, value: string): void => {
    // Store in both sessionStorage (for this tab) and localStorage (as default)
    sessionStorage.setItem(name, value);
    localStorage.setItem(name, value);
  },
  removeItem: (name: string): void => {
    sessionStorage.removeItem(name);
    localStorage.removeItem(name);
  },
};

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    set => ({
      workspaces: [],
      selectedWorkspace: null,
      selectedWorkspaceId: null,
      workspacesLoaded: false,

      setWorkspaces: workspaces => {
        // Atomically set workspaces and resolve selection based on persisted id.
        set(state => {
          const updates: Partial<WorkspaceState> = {
            workspaces,
            workspacesLoaded: true, // Mark as loaded
          };

          const preferredId = state.selectedWorkspaceId;

          // 1) If we have a preferred workspace id, select it if still accessible.
          if (preferredId) {
            const match = workspaces.find(w => w.workspace_id === preferredId) || null;
            if (match) {
              updates.selectedWorkspace = match;
              return updates;
            }

            // Preferred workspace is no longer accessible -> fall back.
            if (workspaces.length > 0) {
              updates.selectedWorkspace = workspaces[0];
              updates.selectedWorkspaceId = workspaces[0].workspace_id;
            } else {
              updates.selectedWorkspace = null;
            }

            return updates;
          }

          // 2) No preferred id: try to keep current selection (if any) but refresh object from list.
          if (state.selectedWorkspace) {
            const currentId = state.selectedWorkspace.workspace_id;
            const match = workspaces.find(w => w.workspace_id === currentId) || null;

            if (match) {
              updates.selectedWorkspace = match;
              updates.selectedWorkspaceId = match.workspace_id;
              return updates;
            }
          }

          // 3) Still nothing selected: auto-select first workspace.
          if (workspaces.length > 0) {
            updates.selectedWorkspace = workspaces[0];
            updates.selectedWorkspaceId = workspaces[0].workspace_id;
          }

          return updates;
        });
      },

      setSelectedWorkspace: workspace => {
        set({
          selectedWorkspace: workspace,
          selectedWorkspaceId: workspace?.workspace_id ?? null,
        });
      },

      clearWorkspace: options => {
        const preserveSelection = options?.preserveSelection ?? false;
        set(state => ({
          workspaces: [],
          selectedWorkspace: null,
          // Keep the preferred workspace id across logout/login if requested.
          selectedWorkspaceId: preserveSelection ? state.selectedWorkspaceId : null,
          workspacesLoaded: false,
        }));
      },
    }),
    {
      name: 'chat-workspace-storage', // App-specific key to avoid conflicts with ai-transform
      version: 2,
      storage: createJSONStorage(() => hybridStorage),
      // Only persist the stable preference (workspace id). Everything else should be
      // refetched/recomputed after auth and workspace list load.
      partialize: state => ({
        selectedWorkspaceId: state.selectedWorkspaceId,
      }),
      // Migrate old persisted shape where we stored the full selectedWorkspace object.
      migrate: (persistedState, version) => {
        if (version >= 2) return persistedState;

        type LegacyWorkspacePersist = {
          selectedWorkspaceId?: string | null;
          selectedWorkspace?: { workspace_id?: string | null } | null;
        };

        const base =
          typeof persistedState === 'object' && persistedState !== null
            ? (persistedState as Record<string, unknown>)
            : ({} as Record<string, unknown>);

        const legacy = base as unknown as LegacyWorkspacePersist;

        const selectedWorkspaceId =
          legacy.selectedWorkspaceId ?? legacy.selectedWorkspace?.workspace_id ?? null;

        return {
          ...base,
          selectedWorkspaceId,
          // Avoid showing stale workspace data before we fetch the new workspace list.
          selectedWorkspace: null,
          workspacesLoaded: false,
        };
      },
    }
  )
);

export const getWorkspaceStoreState = () => useWorkspaceStore.getState();
